export type NativeNodeType =
  | 'paragraph'
  | 'heading'
  | 'bulletList'
  | 'orderedList'
  | 'table'
  | 'blockquote'
  | 'horizontalRule'
  | 'other';

export type NativeMark = {
  type: 'bold' | 'italic' | 'underline' | 'strikethrough' | 'highlight' | 'fontFamily' | 'fontSize';
  value?: string;
};

export type NativeNode = {
  id: string;
  type: NativeNodeType;
  tagName: string;
  html: string;
  text: string;
};

export type NativeDocument = {
  schemaVersion: 1;
  nodes: NativeNode[];
};

export type NativeAnchor = {
  nodeId?: string;
  start?: number;
  end?: number;
  selectedText?: string;
  contextBefore?: string;
  contextAfter?: string;
};

export type NativeComment = {
  id: string;
  authorName: string;
  authorEmail: string;
  body: string;
  mentions: string[];
  createdAt: string;
};

export type NativeCommentThread = {
  id: string;
  anchor: NativeAnchor;
  comments: NativeComment[];
  resolved: boolean;
  createdAt: string;
  resolvedAt?: string;
};

export type NativeSuggestion = {
  id: string;
  authorName: string;
  authorEmail: string;
  originalText: string;
  replacementText: string;
  anchor?: NativeAnchor;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
};

const nativeId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createNativeId = nativeId;

const nodeTypeFor = (tagName: string): NativeNodeType => {
  const tag = tagName.toLowerCase();
  if (/^h[1-6]$/.test(tag)) return 'heading';
  if (tag === 'p' || tag === 'div') return 'paragraph';
  if (tag === 'ul') return 'bulletList';
  if (tag === 'ol') return 'orderedList';
  if (tag === 'table' || tag === 'figure') return 'table';
  if (tag === 'blockquote') return 'blockquote';
  if (tag === 'hr') return 'horizontalRule';
  return 'other';
};

const normalizeRoot = (root: HTMLElement) => {
  const children = Array.from(root.children);
  if (children.length > 0) return children;
  if (root.textContent?.trim()) {
    const paragraph = root.ownerDocument.createElement('p');
    paragraph.textContent = root.textContent;
    root.replaceChildren(paragraph);
    return [paragraph];
  }
  const paragraph = root.ownerDocument.createElement('p');
  paragraph.innerHTML = '<br>';
  root.appendChild(paragraph);
  return [paragraph];
};

export const htmlToNativeDocument = (html: string): NativeDocument => {
  if (typeof DOMParser === 'undefined') {
    return { schemaVersion: 1, nodes: [] };
  }
  const parsed = new DOMParser().parseFromString(html || '<p><br></p>', 'text/html');
  const nodes = normalizeRoot(parsed.body).map((element) => {
    const id = element.getAttribute('data-native-node-id') || nativeId('node');
    element.setAttribute('data-native-node-id', id);
    return {
      id,
      type: nodeTypeFor(element.tagName),
      tagName: element.tagName.toLowerCase(),
      html: element.outerHTML,
      text: element.textContent || '',
    };
  });
  return { schemaVersion: 1, nodes };
};

export const nativeDocumentToHtml = (document: NativeDocument): string =>
  document.nodes.map((node) => node.html).join('');

export const sanitizeNativeCheckpointHtml = (html: string): string => {
  if (typeof DOMParser === 'undefined') return html;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  parsed.querySelectorAll<HTMLElement>('[data-native-suggestion-id]').forEach((element) => {
    if (element.tagName.toLowerCase() === 'del') {
      element.remove();
      return;
    }
    const fragment = parsed.createDocumentFragment();
    while (element.firstChild) fragment.appendChild(element.firstChild);
    element.replaceWith(fragment);
  });
  return parsed.body.innerHTML;
};

export const getCurrentUserIdentity = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user_details') || '{}');
    return {
      name: String(user.name || user.full_name || user.email || 'SOW Studio user'),
      email: String(user.email || '').toLowerCase(),
    };
  } catch {
    return { name: 'SOW Studio user', email: '' };
  }
};
